// src/pages/admin/AdminCleanupPage.tsx - Destructive admin tools: league deletion
// (guarded by a type-the-league-name confirm) and placeholder-stats purge.
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { AdminLeague } from '../../types';
import BackLink from '../../components/common/BackLink';

export default function AdminCleanupPage() {
  const { user } = useAuth();

  const [leagues, setLeagues] = useState<AdminLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<AdminLeague | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [placeholderCount, setPlaceholderCount] = useState<number | null>(null);
  const [placeholderLoading, setPlaceholderLoading] = useState(true);
  const [placeholderConfirming, setPlaceholderConfirming] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const [purgeResult, setPurgeResult] = useState<number | null>(null);

  const refetchLeagues = useCallback(async () => setLeagues(await apiService.getAdminLeagues()), []);

  const refetchPlaceholderCount = useCallback(async () => {
    const { count } = await apiService.getPlaceholderStatsCount();
    setPlaceholderCount(count);
  }, []);

  useEffect(() => {
    if (!user?.is_superuser) return;
    (async () => {
      setLoading(true);
      setPageError(null);
      try {
        await refetchLeagues();
      } catch (e: any) {
        setPageError(e.message || 'Failed to load leagues');
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.is_superuser, refetchLeagues]);

  useEffect(() => {
    if (!user?.is_superuser) return;
    (async () => {
      setPlaceholderLoading(true);
      try {
        await refetchPlaceholderCount();
      } catch (e: any) {
        setPurgeError(e.message || 'Failed to load placeholder stats count');
      } finally {
        setPlaceholderLoading(false);
      }
    })();
  }, [user?.is_superuser, refetchPlaceholderCount]);

  if (!user?.is_superuser) {
    return <Navigate to="/app" replace />;
  }

  const openDelete = (l: AdminLeague) => {
    setDeleteError(null);
    setDeleteConfirmText('');
    setDeleteTarget(l);
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiService.deleteAdminLeague(deleteTarget.id);
      closeDelete();
      await refetchLeagues();
    } catch (e: any) {
      setDeleteError(e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const confirmPurge = async () => {
    setPurging(true);
    setPurgeError(null);
    try {
      const { deleted } = await apiService.purgePlaceholderStats();
      setPurgeResult(deleted);
      await refetchPlaceholderCount();
    } catch (e: any) {
      setPurgeError(e.message || 'Purge failed');
    } finally {
      setPurging(false);
      setPlaceholderConfirming(false);
    }
  };

  return (
    <div>
      <BackLink to="/app/admin">Back to Admin Panel</BackLink>
      <div className="border-b-2 border-ink-900 pb-4 mt-4 mb-8">
        <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Cleanup</h1>
        <p className="text-secondary-600 mt-1">
          Destructive tools. Deletions here are permanent and cannot be undone.
        </p>
      </div>

      {/* SECTION 1 — Leagues */}
      <section className="mb-10">
        <h2 className="font-display uppercase text-ink-900 text-xl leading-none mb-3">Leagues</h2>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="loading-spinner w-6 h-6 mr-3" />
            <span className="text-secondary-500 text-sm">Loading leagues…</span>
          </div>
        )}

        {!loading && pageError && <p className="text-sm text-danger py-4">{pageError}</p>}

        {!loading && !pageError && (
          <>

            {leagues.length === 0 ? (
              <p className="py-12 text-center font-display uppercase tracking-wider text-secondary-400">
                No leagues.
              </p>
            ) : (
              <div className="overflow-x-auto bg-white border border-secondary-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-secondary-500 uppercase tracking-wide text-xs border-b-2 border-ink-900">
                      <th className="py-2.5 px-4 text-left font-semibold">Name</th>
                      <th className="py-2.5 px-4 text-left font-semibold">Status</th>
                      <th className="py-2.5 px-4 text-right font-semibold">Season</th>
                      <th className="py-2.5 px-4 text-right font-semibold">Teams</th>
                      <th className="py-2.5 px-4 text-left font-semibold">Commissioner</th>
                      <th className="py-2.5 px-4 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-200">
                    {leagues.map((l) => (
                      <tr key={l.id} className="text-ink-900">
                        <td className="py-3 px-4 font-medium">{l.name}</td>
                        <td className="py-3 px-4 text-secondary-600 uppercase text-xs">
                          {l.league_status}
                        </td>
                        <td className="py-3 px-4 text-right font-display">{l.season_year}</td>
                        <td className="py-3 px-4 text-right font-display">{l.team_count}</td>
                        <td className="py-3 px-4 text-secondary-600">{l.commissioner || '—'}</td>
                        <td className="py-3 px-4 text-right">
                          <button
                            type="button"
                            aria-label={`Delete ${l.name}`}
                            onClick={() => openDelete(l)}
                            className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-danger text-danger hover:bg-danger hover:text-white"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* SECTION 2 — Placeholder Stats */}
      <section className="border border-secondary-300 bg-white p-6">
        <h2 className="font-display uppercase text-ink-900 text-xl leading-none mb-3">
          Placeholder Stats
        </h2>

        {placeholderLoading && (
          <div className="flex items-center py-4">
            <div className="loading-spinner w-5 h-5 mr-3" />
            <span className="text-secondary-500 text-sm">Loading placeholder stats…</span>
          </div>
        )}

        {!placeholderLoading && (
          <>
            {purgeError && (
              <p className="text-sm text-danger mb-3" role="alert">
                {purgeError}
              </p>
            )}

            {purgeResult !== null && (
              <p className="text-sm text-success mb-3">Deleted {purgeResult} placeholder stat rows.</p>
            )}

            {placeholderCount === 0 ? (
              <p className="py-4 text-center font-display uppercase tracking-wider text-secondary-400">
                No placeholder stats.
              </p>
            ) : (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-ink-900">
                  {`${placeholderCount} season-2099 placeholder stat rows`}
                </p>

                {placeholderConfirming ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-secondary-500">Purge these rows?</span>
                    <button
                      type="button"
                      disabled={purging}
                      onClick={confirmPurge}
                      className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider bg-danger text-white hover:bg-danger/90 disabled:opacity-50"
                    >
                      {purging ? 'Purging…' : 'Confirm Purge'}
                    </button>
                    <button
                      type="button"
                      disabled={purging}
                      onClick={() => setPlaceholderConfirming(false)}
                      className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-secondary-300 text-secondary-600 hover:text-ink-900 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPlaceholderConfirming(true)}
                    className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-danger text-danger hover:bg-danger hover:text-white"
                  >
                    Purge
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4">
          <div
            className="w-full max-w-md border border-secondary-300 bg-white p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-league-heading"
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !deleting) {
                closeDelete();
              }
            }}
          >
            <h3
              id="delete-league-heading"
              className="font-display uppercase text-xl leading-none text-danger-700"
            >
              Delete League
            </h3>
            <p className="mt-3 text-sm text-secondary-700">
              This permanently removes the league and all of its teams, rosters, and history. Type the
              league name <span className="font-semibold text-ink-900">{deleteTarget.name}</span> to
              confirm.
            </p>
            <input
              type="text"
              aria-label="Type the league name to confirm"
              autoFocus
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="form-input mt-4 w-full"
            />
            {deleteError && (
              <p className="mt-3 text-sm text-danger-600" role="alert">
                {deleteError}
              </p>
            )}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                className="btn-outline-secondary flex-1"
                onClick={closeDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger flex-1"
                onClick={confirmDelete}
                disabled={deleting || deleteConfirmText !== deleteTarget.name}
              >
                {deleting ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
